import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";

const AuthCtx = createContext({ user: null, session: null, ready: false, login: async () => {}, logout: async () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Cookie is httpOnly — a 401 just means "not signed in". Silent refresh
    // (via axios interceptor) transparently rotates the cookie before this
    // resolves if a valid refresh_token is present.
    http.get("/auth/me")
      .then((r) => { setUser(r.data.user); setSession(r.data.session || null); })
      .catch(() => { setUser(null); setSession(null); })
      .finally(() => setReady(true));
  }, []);

  // Session is a moving target because the axios interceptor silently rotates
  // tokens on 401. Re-hydrate every 5 minutes so the countdown badge stays
  // accurate through long editing sessions.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      http.get("/auth/me")
        .then((r) => { setUser(r.data.user); setSession(r.data.session || null); })
        .catch(() => { /* interceptor already handled 401; leave state alone */ });
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const value = useMemo(() => {
    const login = async (email, password) => {
      const { data } = await http.post("/auth/login", { email, password });
      setUser(data.user);
      setSession(data.session || null);
      return data.user;
    };
    const logout = async () => {
      try { await http.post("/auth/logout"); } catch (e) { /* best-effort */ }
      setUser(null);
      setSession(null);
    };
    return { user, session, ready, login, logout };
  }, [user, session, ready]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
